import React from "react";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import { formatMcpDate } from "../../utils/HelperFunction/mcpHelpers";

const VISIBLE_TOOLS = 3;

const toolChips = (key, text) => {
  if (key.tools.length === 0) {
    return (
      <Typography variant='body2' color='text.secondary'>
        {text.NO_TOOLS}
      </Typography>
    );
  }

  const hidden = key.tools.length - VISIBLE_TOOLS;
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
      {key.tools.slice(0, VISIBLE_TOOLS).map((tool) => (
        <Chip
          key={tool}
          size='small'
          variant='outlined'
          label={tool}
          sx={{ fontFamily: "monospace", fontSize: "0.72rem" }}
        />
      ))}
      {hidden > 0 && (
        <Tooltip title={key.tools.slice(VISIBLE_TOOLS).join(", ")}>
          <Chip size='small' label={`+${hidden}`} />
        </Tooltip>
      )}
    </Box>
  );
};

const McpKeyList = ({
  keys,
  text,
  onViewDetails,
  onDelete,
  onCreate,
  busy = false,
}) => {
  if (keys.length === 0) {
    return (
      <Paper
        variant='outlined'
        sx={{ py: 5, px: 2, textAlign: "center", borderStyle: "dashed" }}
      >
        <Typography variant='subtitle1' fontWeight={600}>
          {text.EMPTY_STATE}
        </Typography>
        <Typography variant='body2' color='text.secondary' sx={{ mt: 0.5 }}>
          {text.EMPTY_HINT}
        </Typography>
        {onCreate && (
          <Button
            variant='contained'
            onClick={onCreate}
            sx={{ mt: 2, backgroundColor: "#003D78", textTransform: "none" }}
          >
            {text.CREATE_BUTTON}
          </Button>
        )}
      </Paper>
    );
  }

  return (
    <TableContainer component={Paper} variant='outlined'>
      <Table size='small'>
        <TableHead>
          <TableRow>
            <TableCell>{text.COLUMN_NAME}</TableCell>
            <TableCell>{text.COLUMN_TOOLS}</TableCell>
            <TableCell>{text.COLUMN_CREATED}</TableCell>
            <TableCell>{text.COLUMN_STATUS}</TableCell>
            <TableCell align='right'>{text.COLUMN_ACTIONS}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {keys.map((key) => (
            <TableRow key={key.id} hover>
              <TableCell>
                <Typography variant='body2' fontWeight={600}>
                  {key.name}
                </Typography>
                {key.keyPrefix && (
                  <Typography
                    variant='caption'
                    color='text.secondary'
                    sx={{ fontFamily: "monospace" }}
                  >
                    {key.keyPrefix}…
                  </Typography>
                )}
              </TableCell>
              <TableCell>{toolChips(key, text)}</TableCell>
              <TableCell sx={{ whiteSpace: "nowrap" }}>
                {formatMcpDate(key.createdAt)}
              </TableCell>
              <TableCell>
                <Chip
                  size='small'
                  color={key.isExpired ? "warning" : "success"}
                  label={key.isExpired ? text.STATUS_EXPIRED : text.STATUS_ACTIVE}
                />
              </TableCell>
              <TableCell align='right' sx={{ whiteSpace: "nowrap" }}>
                <Tooltip title={text.VIEW_DETAILS}>
                  <IconButton
                    size='small'
                    onClick={() => onViewDetails(key)}
                    aria-label={`${text.VIEW_DETAILS} ${key.name}`}
                  >
                    <InfoOutlinedIcon fontSize='small' />
                  </IconButton>
                </Tooltip>
                <Tooltip title={text.DELETE}>
                  <span>
                    <IconButton
                      size='small'
                      color='error'
                      disabled={busy}
                      onClick={() => onDelete(key)}
                      aria-label={`${text.DELETE} ${key.name}`}
                    >
                      <DeleteOutlineIcon fontSize='small' />
                    </IconButton>
                  </span>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default McpKeyList;
